import { ArrowLeft, Check, ListMusic, MessageCircle, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../api';
import { useAuth } from '../../auth-context';
import { t } from '../../i18n';
import type {
  MemorizationToday,
  MemSlot,
  PrayerRow,
  PrayerTime,
  RecordIntent,
  TodayResponse,
} from '../../types';
import { AppShell } from '../shared/AppShell';
import { Avatar } from '../shared/Avatar';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import { Modal } from '../shared/Modal';
import { Pill } from '../shared/Pill';
import { ProgressTrack } from '../shared/ProgressTrack';
import { StatusCircle } from '../shared/StatusCircle';
import { useToast } from '../shared/Toast';
import { ParentMessageModal } from './ParentMessageModal';
import { TodayRecordingsModal } from './TodayRecordings';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

export function ChildDashboard() {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const child = state.status === 'child' ? state.child : null;

  const [data, setData] = useState<TodayResponse | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [showMessages, setShowMessages] = useState(true);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [confirm, setConfirm] = useState<
    | null
    | { kind: 'complete'; prayer: PrayerTime; count: number }
    | { kind: 'skip'; prayer: PrayerTime }
    | { kind: 'reopen'; prayer: PrayerTime }
  >(null);

  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    api.today().then((res) => {
      if (!cancelled) setData(res);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  if (!child) return null;

  const onLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const completeOrSkip = async (prayer: PrayerTime, was_skipped: boolean) => {
    try {
      await api.completePrayer(prayer, was_skipped);
      setConfirm(null);
      toast.show('Barakalloh', 'success');
      refresh();
    } catch (err) {
      setConfirm(null);
      const msg = err instanceof ApiError ? err.message : t('common.error.network');
      toast.show(msg, 'danger');
    }
  };

  const reopen = async (prayer: PrayerTime) => {
    try {
      await api.reopenPrayer(prayer);
      setConfirm(null);
      refresh();
    } catch (err) {
      setConfirm(null);
      const msg = err instanceof ApiError ? err.message : t('common.error.network');
      toast.show(msg, 'danger');
    }
  };

  const startMemorization = (slot: string) => {
    navigate('/child/record', { state: { mode: 'memorization', slot } as RecordIntent });
  };

  const currentPrayer = data?.current_prayer ?? null;
  const currentPrayerRow = data?.prayers.find((p) => p.prayer_time === currentPrayer);
  const allDone = data && currentPrayer === null;
  const recitationActive = data ? data.recitation_active !== false : true;
  const memorization = data?.memorization ?? null;
  // Show module headings only when the child has more than one module to do.
  const multiModule = !!(recitationActive && memorization);

  return (
    <AppShell ramp={child.color_ramp}>
      {data && data.unseen_parent_messages > 0 && showMessages ? (
        <ParentMessageModal onAllSeen={() => { setShowMessages(false); refresh(); }} />
      ) : null}

      <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 p-5">
        <header className="flex items-center justify-between">
          <button
            onClick={onLogout}
            className="flex items-center gap-1 text-[13px] text-ink-muted active:scale-[0.98]"
          >
            <ArrowLeft size={16} />
            Profil
          </button>
          <button
            onClick={() => navigate('/child/profile')}
            aria-label={t('profile.title')}
            className="text-ink-muted active:scale-[0.98]"
          >
            <User size={20} />
          </button>
        </header>

        <section className="flex items-center gap-3">
          <Avatar
            initial={child.avatar_initial}
            ramp={child.color_ramp}
            size="md"
            src={data?.avatar_url ?? child.avatar_url ?? undefined}
          />
          <div className="flex flex-col">
            <span className="text-[18px] font-medium">
              {t('dashboard.greeting', { name: child.name })}
            </span>
            {data ? (
              <span className="text-[12px] text-ink-muted">
                {data.streak_days > 0
                  ? t('dashboard.streak', { n: data.streak_days })
                  : t('dashboard.streak.zero')}
              </span>
            ) : null}
          </div>
        </section>

        {!data ? (
          <Card>
            <div className="h-[140px] animate-pulse rounded bg-surface-muted" />
          </Card>
        ) : null}

        {/* ── Module: Takrorlash (recitation) ───────────────────────────── */}
        {data && recitationActive ? (
          <section className="flex flex-col gap-4">
            {multiModule ? (
              <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
                {t('module.recitation')}
              </div>
            ) : null}

            {allDone ? (
              <Card variant="child">
                <div className="text-[11px] font-medium uppercase tracking-wide text-child-text/70">
                  {t('dashboard.now')}
                </div>
                <div className="mt-2 text-[20px] font-medium">{t('dashboard.day_done')}</div>
                <div className="mt-2 text-[12px] text-child-text/70">
                  {t('dashboard.day_done.hint')}
                </div>
              </Card>
            ) : currentPrayerRow ? (
              <Card variant="child">
                <div className="flex flex-col gap-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-child-text/70">
                    {t('dashboard.now')}
                  </div>
                  <div>
                    <div className="text-[20px] font-medium">
                      {t(PRAYER_NAME_KEY[currentPrayerRow.prayer_time])}
                    </div>
                    <div className="text-[13px] text-child-text/80">
                      {currentPrayerRow.recitation_count > 0
                        ? t('dashboard.continue', { surah: data.current_surah.name_uz })
                        : t('dashboard.starting')}
                    </div>
                  </div>
                  <Button childColor fullWidth onClick={() => navigate('/child/surah-picker')}>
                    {t('dashboard.start')}
                  </Button>
                  <div className="flex gap-2">
                    {currentPrayerRow.recitation_count > 0 ? (
                      <Button
                        variant="secondary"
                        childColor
                        onClick={() => setConfirm({
                          kind: 'complete',
                          prayer: currentPrayerRow.prayer_time,
                          count: currentPrayerRow.recitation_count,
                        })}
                        className="flex-1"
                      >
                        {t('prayer.complete.button')}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        fullWidth
                        onClick={() => setConfirm({ kind: 'skip', prayer: currentPrayerRow.prayer_time })}
                      >
                        {t('prayer.skip.button')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ) : null}

            <div className="flex items-center justify-between rounded-sm bg-warning-bg px-3 py-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-warning">
                {t('dashboard.rule')}
              </span>
              <span className="text-[13px] text-warning">{data.daily_rule_text}</span>
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
                {t('dashboard.today')}
              </div>
              <ul className="flex flex-col">
                {data.prayers.map((p) => (
                  <PrayerListRow
                    key={p.prayer_time}
                    row={p}
                    isCurrent={p.prayer_time === currentPrayer}
                    onReopen={
                      p.status === 'done' || p.status === 'skipped'
                        ? () => setConfirm({ kind: 'reopen', prayer: p.prayer_time })
                        : undefined
                    }
                  />
                ))}
              </ul>
            </div>
          </section>
        ) : null}

        {/* ── Module: Yodlash (memorization) ────────────────────────────── */}
        {data && memorization ? (
          <section className="flex flex-col gap-3">
            {multiModule ? (
              <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
                {t('module.memorization')}
              </div>
            ) : null}
            <MemorizationCard mem={memorization} onStart={startMemorization} />
          </section>
        ) : null}

        {/* ── Today's recordings (all modules) ──────────────────────────── */}
        {data &&
        (data.prayers.some((p) => p.recitation_count > 0) ||
          (memorization && memorization.total_count > 0)) ? (
          <button
            onClick={() => setRecordsOpen(true)}
            className="flex items-center gap-2 self-start rounded-md px-1 py-2 text-[12px] text-ink-muted active:scale-[0.98]"
          >
            <ListMusic size={14} />
            {t('child.records.open')}
          </button>
        ) : null}

        {/* ── Messages ──────────────────────────────────────────────────── */}
        {data && data.unseen_parent_messages > 0 ? (
          <button
            onClick={() => navigate('/child/messages')}
            className="flex items-center gap-3 rounded-md bg-surface-muted px-3 py-3 text-left active:scale-[0.99]"
          >
            <span className="relative inline-flex">
              <MessageCircle size={20} />
              <span className="absolute -right-1 -top-1 inline-block h-2 w-2 rounded-full bg-danger" />
            </span>
            <span className="text-[13px] text-ink">{t('messages.from_parent')}</span>
          </button>
        ) : null}
      </main>

      <TodayRecordingsModal
        open={recordsOpen}
        onClose={() => setRecordsOpen(false)}
        onChanged={refresh}
      />

      <Modal
        open={confirm?.kind === 'complete'}
        onClose={() => setConfirm(null)}
        title={
          confirm?.kind === 'complete'
            ? t('prayer.complete.confirm', { prayer: t(PRAYER_NAME_KEY[confirm.prayer]) })
            : undefined
        }
      >
        {confirm?.kind === 'complete' ? (
          <div className="flex flex-col gap-4">
            <p className="text-[13px] text-ink-muted">
              {t('prayer.complete.subtitle', { n: confirm.count })}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                fullWidth
                onClick={() => completeOrSkip(confirm.prayer, false)}
              >
                {t('prayer.complete.yes')}
              </Button>
              <Button variant="ghost" fullWidth onClick={() => setConfirm(null)}>
                {t('prayer.complete.no')}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={confirm?.kind === 'reopen'}
        onClose={() => setConfirm(null)}
      >
        {confirm?.kind === 'reopen' ? (
          <div className="flex flex-col gap-4">
            <p className="text-[14px]">
              {t('prayer.reopen.confirm', { prayer: t(PRAYER_NAME_KEY[confirm.prayer]) })}
            </p>
            <div className="flex flex-col gap-2">
              <Button fullWidth onClick={() => reopen(confirm.prayer)}>
                {t('prayer.reopen.yes')}
              </Button>
              <Button variant="ghost" fullWidth onClick={() => setConfirm(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={confirm?.kind === 'skip'}
        onClose={() => setConfirm(null)}
      >
        {confirm?.kind === 'skip' ? (
          <div className="flex flex-col gap-4">
            <p className="text-[14px]">
              {t('prayer.skip.confirm', { prayer: t(PRAYER_NAME_KEY[confirm.prayer]) })}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                fullWidth
                onClick={() => completeOrSkip(confirm.prayer, true)}
              >
                {t('common.confirm')}
              </Button>
              <Button variant="ghost" fullWidth onClick={() => setConfirm(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </AppShell>
  );
}

function MemorizationCard({
  mem,
  onStart,
}: {
  mem: MemorizationToday;
  onStart: (slot: string) => void;
}) {
  const current = mem.current_slot ? mem.slots.find((s) => s.time === mem.current_slot) : null;
  const allDone = mem.slots.length > 0 && mem.slots.every((s) => s.status === 'done');
  const firstUpcoming = mem.slots.find((s) => s.status === 'upcoming');

  return (
    <Card variant="child">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[20px] font-medium">{mem.surah.name_uz}</div>
            <div className="text-[13px] text-child-text/80">
              {t('mem.verse_range', { from: mem.ayah_from, to: mem.ayah_to })}
            </div>
          </div>
          {mem.surah.name_ar ? (
            <div className="font-arabic text-[26px] leading-none text-child-text/90" dir="rtl">
              {mem.surah.name_ar}
            </div>
          ) : null}
        </div>

        {current ? (
          <>
            <div className="text-[11px] font-medium uppercase tracking-wide text-child-text/70">
              {t('mem.now', { time: current.time })}
            </div>
            <div className="flex items-center justify-between">
              <ProgressTrack
                target={current.target}
                done={current.count}
                status={
                  current.count >= current.target
                    ? 'done'
                    : current.count > 0
                      ? 'in_progress'
                      : 'pending'
                }
              />
              <span className="text-[12px] tabular-nums text-child-text/70">
                {current.count}/{current.target}
              </span>
            </div>
            <Button childColor fullWidth onClick={() => onStart(current.time)}>
              {t('mem.start')}
            </Button>
          </>
        ) : allDone ? (
          <div className="text-[14px]">{t('mem.all_done')}</div>
        ) : firstUpcoming ? (
          <div className="text-[13px] text-child-text/70">
            {t('mem.before_first', { time: firstUpcoming.time })}
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5 pt-1">
          <div className="text-[11px] uppercase tracking-wide text-child-text/60">
            {t('mem.slots_label')}
          </div>
          <div className="flex flex-wrap gap-2">
            {mem.slots.map((s) => (
              <SlotChip key={s.time} slot={s} onTap={s.is_due ? () => onStart(s.time) : undefined} />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function SlotChip({ slot, onTap }: { slot: MemSlot; onTap?: () => void }) {
  const dotClass =
    slot.status === 'done'
      ? 'bg-child-accent'
      : slot.status === 'in_progress'
        ? 'bg-child-accent/50'
        : slot.status === 'open'
          ? 'border border-child-accent'
          : 'border border-ink-subtle/40';

  const className = [
    'flex items-center gap-1.5 rounded-full border-hairline px-2.5 py-1 text-[12px] tabular-nums',
    onTap ? 'border-border bg-surface active:scale-[0.97]' : 'border-transparent opacity-50',
    slot.is_current ? 'ring-1 ring-child-accent' : '',
  ].join(' ');

  const inner = (
    <>
      <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
      <span>{slot.time}</span>
      {slot.status === 'done' ? <Check size={12} className="text-child-accent" /> : null}
    </>
  );

  if (onTap) {
    return (
      <button type="button" onClick={onTap} className={className}>
        {inner}
      </button>
    );
  }
  return <span className={className}>{inner}</span>;
}

function PrayerListRow({
  row,
  isCurrent,
  onReopen,
}: {
  row: PrayerRow;
  isCurrent: boolean;
  onReopen?: () => void;
}) {
  const inner = (
    <>
      <StatusCircle status={row.status} isOptional={row.is_optional} />
      <span className="flex-1 text-[14px]">
        {t(PRAYER_NAME_KEY[row.prayer_time])}
        {row.is_optional ? (
          <Pill className="ml-2" tone="neutral">
            {t('dashboard.optional')}
          </Pill>
        ) : null}
      </span>
      <ProgressTrack target={row.target} done={row.recitation_count} status={row.status} />
    </>
  );

  const baseClass = ['flex items-center gap-3 py-2', isCurrent ? 'font-medium' : ''].join(' ');

  if (onReopen) {
    return (
      <li>
        <button
          type="button"
          onClick={onReopen}
          aria-label={t('prayer.reopen.aria', { prayer: t(PRAYER_NAME_KEY[row.prayer_time]) })}
          className={`${baseClass} w-full text-left active:scale-[0.99]`}
        >
          {inner}
        </button>
      </li>
    );
  }

  return <li className={baseClass}>{inner}</li>;
}
